import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { InsurancePolicyTypeCopyMap } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { getDisplayNameFromEntityType } from '@/modules/entities/utils/getDisplayNameFromEntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { HouseholdChat_MessageFragment } from '@/modules/household/chat/graphql/HouseholdChat.generated';
import {
  AiSuggestionDispositiveProvisionDeathEvent,
  ClientOrganizationKind,
  ClientProfileRelationshipType,
  DispositiveProvisionDispositionKind,
  DispositiveProvisionRecipientKind,
  DispositiveProvisionTransferTaxKind,
  EntityKind,
  TestamentaryEntityKind,
  TransferMethod,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import { formatCurrency } from '@/utils/formatting/currency';
import { formatPercentSmart } from '@/utils/formatting/percent';
import { pluralize } from '@/utils/formatting/strings';

export type ApplicationRecordChanges = NonNullable<
  NonNullable<
    HouseholdChat_MessageFragment['confirmationContent']
  >['applicationRecordChanges']
>;

type Reference = { displayName: string };
type ProvisionValues = Pick<
  ApplicationRecordChanges['dispositionScenarioGroups'][number]['provisions'][number],
  'recipient' | 'share' | 'transferMethod' | 'transferTaxKind' | 'bearsTax'
>;

const ORGANIZATION_KIND_LABELS: Record<ClientOrganizationKind, string> = {
  [ClientOrganizationKind.CharitableOrganization]: 'Charitable organization',
  [ClientOrganizationKind.CorporateTrustee]: 'Corporate trustee',
  [ClientOrganizationKind.Other]: 'Other organization',
};

const TESTAMENTARY_ENTITY_KIND_LABELS: Record<TestamentaryEntityKind, string> =
  {
    [TestamentaryEntityKind.CharitableEntity]: 'Charitable entity',
    [TestamentaryEntityKind.CharitableTrust]: 'Charitable trust',
    [TestamentaryEntityKind.GrantorEstate]: 'Grantor estate',
    [TestamentaryEntityKind.InheritedRetirementAccount]:
      'Inherited retirement account',
    [TestamentaryEntityKind.TestamentaryTrust]: 'Sub-trust',
  };

const RELATIONSHIP_LABELS: Record<ClientProfileRelationshipType, string> = {
  [ClientProfileRelationshipType.Child]: 'Child',
  [ClientProfileRelationshipType.ChildInLaw]: 'Child-in-law',
  [ClientProfileRelationshipType.Cousin]: 'Cousin',
  [ClientProfileRelationshipType.CousinInLaw]: 'Cousin-in-law',
  [ClientProfileRelationshipType.DomesticPartner]: 'Domestic partner',
  [ClientProfileRelationshipType.ExChildInLaw]: 'Former child-in-law',
  [ClientProfileRelationshipType.ExCousinInLaw]: 'Former cousin-in-law',
  [ClientProfileRelationshipType.ExDomesticPartner]: 'Former domestic partner',
  [ClientProfileRelationshipType.ExGrandchildInLaw]: 'Former grandchild-in-law',
  [ClientProfileRelationshipType.ExGreatGrandchildInLaw]:
    'Former great-grandchild-in-law',
  [ClientProfileRelationshipType.ExGreatGreatGrandchildInLaw]:
    'Former great-great-grandchild-in-law',
  [ClientProfileRelationshipType.ExNephewNieceInLaw]:
    'Former nephew/niece-in-law',
  [ClientProfileRelationshipType.ExSiblingInLaw]: 'Former sibling-in-law',
  [ClientProfileRelationshipType.ExSpouse]: 'Former spouse',
  [ClientProfileRelationshipType.ExUncleAuntInLaw]: 'Former uncle/aunt-in-law',
  [ClientProfileRelationshipType.Friend]: 'Friend',
  [ClientProfileRelationshipType.Grandchild]: 'Grandchild',
  [ClientProfileRelationshipType.GrandchildInLaw]: 'Grandchild-in-law',
  [ClientProfileRelationshipType.Grandparent]: 'Grandparent',
  [ClientProfileRelationshipType.GreatGrandchild]: 'Great-grandchild',
  [ClientProfileRelationshipType.GreatGrandchildInLaw]:
    'Great-grandchild-in-law',
  [ClientProfileRelationshipType.GreatGrandparent]: 'Great-grandparent',
  [ClientProfileRelationshipType.GreatGreatGrandchild]:
    'Great-great-grandchild',
  [ClientProfileRelationshipType.GreatGreatGrandchildInLaw]:
    'Great-great-grandchild-in-law',
  [ClientProfileRelationshipType.NephewNiece]: 'Nephew/niece',
  [ClientProfileRelationshipType.NephewNieceInLaw]: 'Nephew/niece-in-law',
  [ClientProfileRelationshipType.OtherNonRelative]: 'Other non-relative',
  [ClientProfileRelationshipType.OtherRelative]: 'Other relative',
  [ClientProfileRelationshipType.Parent]: 'Parent',
  [ClientProfileRelationshipType.Sibling]: 'Sibling',
  [ClientProfileRelationshipType.SiblingInLaw]: 'Sibling-in-law',
  [ClientProfileRelationshipType.Spouse]: 'Spouse',
  [ClientProfileRelationshipType.StepChild]: 'Stepchild',
  [ClientProfileRelationshipType.StepParent]: 'Stepparent',
  [ClientProfileRelationshipType.UncleAunt]: 'Uncle/aunt',
  [ClientProfileRelationshipType.UncleAuntInLaw]: 'Uncle/aunt-in-law',
};

const SHARE_KIND_LABELS: Record<DispositiveProvisionDispositionKind, string> = {
  [DispositiveProvisionDispositionKind.Amount]: 'Amount',
  [DispositiveProvisionDispositionKind.AnythingLeftOver]: 'Remainder',
  [DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption]:
    'Lesser of remaining state or federal exemption',
  [DispositiveProvisionDispositionKind.Percentage]: 'Percentage',
  [DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption]:
    'Remaining federal lifetime exemption in excess of state exemption',
  [DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantor]:
    'Remaining GST exclusion of grantor',
  [DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion]:
    'Remaining GST exclusion of grantor in excess of lifetime exclusion',
  [DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor]:
    'Remaining lifetime exclusion of grantor',
  [DispositiveProvisionDispositionKind.RemainingStateExemption]:
    'Remaining state exemption',
};

const TRANSFER_METHOD_LABELS: Record<TransferMethod, string> = {
  [TransferMethod.InKind]: 'In kind',
  [TransferMethod.LiquidateToCash]: 'Liquidate to cash',
};

const TRANSFER_TAX_KIND_LABELS: Record<
  DispositiveProvisionTransferTaxKind,
  string
> = {
  [DispositiveProvisionTransferTaxKind.Charitable]: 'Charitable',
  [DispositiveProvisionTransferTaxKind.Generation_2OrOtherIndividual]:
    'Generation 2 or other individual',
  [DispositiveProvisionTransferTaxKind.Generation_2Then_3]:
    'Generation 2, then generation 3',
  [DispositiveProvisionTransferTaxKind.Generation_3]: 'Generation 3',
  [DispositiveProvisionTransferTaxKind.Pourover]: 'Pourover',
  [DispositiveProvisionTransferTaxKind.SpouseCreditShelter]:
    'Spouse credit shelter',
  [DispositiveProvisionTransferTaxKind.SpouseFederalCreditShelterStateMaritalExclusion]:
    'Spouse federal credit shelter and state marital exclusion',
  [DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion]:
    'Spouse marital exclusion',
};

export function formatOptionalCurrency(
  value: ApplicationRecordChanges['assets'][number]['valueUSD']
) {
  return value == null ? '—' : formatCurrency(value, { notation: 'compact' });
}

export function referenceLabel(value: Reference | null | undefined) {
  return value?.displayName ?? '—';
}

export function referenceListLabel(values: Reference[]) {
  return values.length ? values.map(referenceLabel).join(', ') : '—';
}

export function entityKindLabel(kind: EntityKind) {
  return getDisplayNameFromEntityType(getEntityTypeFromEntityKind(kind));
}

export function testamentaryEntityKindLabel(kind: TestamentaryEntityKind) {
  return TESTAMENTARY_ENTITY_KIND_LABELS[kind];
}

export function organizationKindLabel(
  kind: ClientOrganizationKind | null | undefined
) {
  return kind == null ? 'Organization' : ORGANIZATION_KIND_LABELS[kind];
}

export function relationshipLabel(kind: ClientProfileRelationshipType) {
  return RELATIONSHIP_LABELS[kind];
}

export function insurancePolicyKindLabel(
  kind: keyof typeof InsurancePolicyTypeCopyMap
) {
  return InsurancePolicyTypeCopyMap[kind];
}

export function shareLabel(share: ProvisionValues['share']) {
  let value: string;
  switch (share.kind) {
    case DispositiveProvisionDispositionKind.Percentage:
      value = `${share.percentage ? formatPercentSmart(share.percentage) : EMPTY_CONTENT_HYPHEN}%`;
      break;
    case DispositiveProvisionDispositionKind.Amount:
      value = formatOptionalCurrency(share.amountUSD);
      break;
    case DispositiveProvisionDispositionKind.AnythingLeftOver:
    case DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption:
    case DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption:
    case DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantor:
    case DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion:
    case DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor:
    case DispositiveProvisionDispositionKind.RemainingStateExemption:
      value = SHARE_KIND_LABELS[share.kind];
      break;
    default:
      throw new UnreachableError({
        case: share.kind,
        message: 'Unexpected dispositive-provision share kind',
      });
  }

  if (share.formulaicPercentage) {
    value += ` at ${formatPercentSmart(share.formulaicPercentage)}%`;
  }
  if (share.limitUSD) {
    value += `, limited to ${formatOptionalCurrency(share.limitUSD)}`;
  }
  return share.growByInflation ? `${value}, inflation adjusted` : value;
}

export function recipientLabel(recipient: ProvisionValues['recipient']) {
  switch (recipient.kind) {
    case DispositiveProvisionRecipientKind.SurvivingSpouse:
      return 'Surviving spouse';
    case DispositiveProvisionRecipientKind.Entity:
    case DispositiveProvisionRecipientKind.Individual:
    case DispositiveProvisionRecipientKind.Organization:
    case DispositiveProvisionRecipientKind.TestamentaryEntity:
      return referenceLabel(recipient.record);
    default:
      throw new UnreachableError({
        case: recipient.kind,
        message: 'Unexpected dispositive-provision recipient kind',
      });
  }
}

export function transferLabel(provision: ProvisionValues) {
  return `${TRANSFER_METHOD_LABELS[provision.transferMethod]} - ${
    TRANSFER_TAX_KIND_LABELS[provision.transferTaxKind]
  }${provision.bearsTax ? ' - Bears tax' : ''}`;
}

export function provisionContextLabel(
  clientName: string,
  deathEvent: AiSuggestionDispositiveProvisionDeathEvent
) {
  switch (deathEvent) {
    case AiSuggestionDispositiveProvisionDeathEvent.UponFirstDeath:
      return `At first death, if ${clientName} dies first`;
    case AiSuggestionDispositiveProvisionDeathEvent.UponSecondDeath:
      return `At second death, if ${clientName} dies first`;
    case AiSuggestionDispositiveProvisionDeathEvent.None:
      return `No death event specified for ${clientName}`;
    default:
      throw new UnreachableError({
        case: deathEvent,
        message: 'Unexpected dispositive-provision death event',
      });
  }
}

export function getApplicationRecordChangesSubtitle(
  changes: ApplicationRecordChanges
) {
  const count =
    changes.people.length +
    changes.organizations.length +
    changes.entities.length +
    changes.testamentaryEntities.length +
    changes.assets.length +
    changes.lifeInsurancePolicies.length +
    changes.liabilities.length +
    changes.dispositionScenarioGroups.length +
    changes.beneficiaryDesignationGroups.length;
  return `${pluralize({ count, singular: 'change' })}. Nothing is written to the household until you confirm.`;
}
